// Hardfin Confidential - Restricted
import { FilterMenuButton } from '@/components/filter-menu-button';
import { FilterMenuItem } from '@/components/filter-menu-item';
import { KeyboardArrowDown } from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useScrollIndicator } from '@/hooks/use-scroll-indicator';
import { useWorkflows } from '@/workflow/hooks/use-workflows';
import {
  Box,
  CircularProgress,
  Divider,
  Menu,
  TextField,
  Typography,
} from '@mui/material';
import { groupBy, sortBy, sumBy, uniq, without } from 'lodash-es';
import { useMemo, useRef, useState } from 'react';

const DEFAULT_LABEL = 'Workflow stage';

interface Props {
  /**
   * An array of strings representing the currently selected workflow stage IDs.
   */
  value: string[];

  /**
   * The label for the filter menu.
   */
  label?: string;

  /**
   * A callback fired when the selected workflow stage IDs change.
   */
  onChange: (value: string[]) => void;

  /**
   * Whether to show the search input. Defaults to true.
   */
  enableSearch?: boolean;
}

/**
 * A controlled filter menu for selecting zero or more workflow stages from the remote
 * API. The workflow stages are grouped by workflow, and selecting a
 * group will select all of the workflow stages in that group. Includes search functionality
 * for both workflow and workflow stage names.
 */
export function WorkflowStageFilter(props: Props) {
  const { data: workflows = [], isLoading } = useWorkflows();
  const [searchTerm, setSearchTerm] = useState('');

  const menu = useDisclosure();
  const ref = useRef<HTMLButtonElement>(null);
  const scrollIndicator = useScrollIndicator();

  const label = props.label ?? DEFAULT_LABEL;

  const options = useMemo(
    () =>
      workflows.flatMap((workflow) => {
        // Sort stages by orderIndex to determine position
        const sortedStages = workflow.stages
          .slice()
          .sort((a, b) => a.orderIndex - b.orderIndex);
        const totalStages = sortedStages.length;

        return sortedStages.map((stage, index) => ({
          id: stage.id,
          label: stage.name,
          group: workflow.name,
          orderIndex: stage.orderIndex,
          position: index + 1, // 1-based position
          totalStages,
          isActive: props.value.includes(stage.id),
        }));
      }),
    [workflows, props.value]
  );

  const filteredOptions = useMemo(
    () =>
      options.filter(
        (option) =>
          option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          option.group.toLowerCase().includes(searchTerm.toLowerCase()) ||
          option.position.toString().includes(searchTerm) ||
          `${option.position}/${option.totalStages}`.includes(searchTerm)
      ),
    [options, searchTerm]
  );

  const groups = useMemo(
    () =>
      groupBy(
        sortBy(filteredOptions, (option) => option.position),
        'group'
      ),
    [filteredOptions]
  );

  const sortedGroupLabels = useMemo(
    () => sortBy(Object.keys(groups), (group) => group.toLowerCase()),
    [groups]
  );

  const count = useMemo(
    () => sumBy(filteredOptions, 'isActive'),
    [filteredOptions]
  );
  const isDisabled = useMemo(
    () => options.length === 0 || isLoading,
    [options.length, isLoading]
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleMenuClose = () => {
    menu.close();
    setSearchTerm('');
  };

  const handleScrollToBottom = () => {
    if (scrollIndicator.element) {
      scrollIndicator.element.scrollTo({
        top: scrollIndicator.element.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      <FilterMenuButton
        ref={ref}
        count={count}
        onClick={menu.open}
        isDisabled={isDisabled}
      >
        {label}
      </FilterMenuButton>
      <Menu open={menu.isOpen} anchorEl={ref.current} onClose={handleMenuClose}>
        <Box sx={{ position: 'relative' }}>
          {(props.enableSearch ?? true) && (
            <div style={{ padding: '8px' }}>
              <TextField
                size="small"
                placeholder="Search workflow stages..."
                value={searchTerm}
                onChange={handleSearchChange}
                sx={{ width: '100%', marginBottom: 1 }}
                inputProps={{
                  'aria-label':
                    'Search workflow stages by name, workflow, or position',
                }}
              />
            </div>
          )}
          <div
            ref={scrollIndicator.ref}
            style={{
              maxHeight: '400px',
              overflowY: 'auto',
              position: 'relative',
            }}
          >
            {isLoading ? (
              <div style={{ padding: '16px', textAlign: 'center' }}>
                <CircularProgress size={24} />
              </div>
            ) : (
              sortedGroupLabels.map((groupLabel, index) => {
                const groupOptions = groups[groupLabel];
                const activeOptions = groupOptions.filter(
                  ({ isActive }) => isActive
                );
                const isChecked = activeOptions.length === groupOptions.length;
                const isIndeterminate =
                  activeOptions.length > 0 &&
                  activeOptions.length < groupOptions.length;

                const handleChangeGroup = (isActive: boolean) => {
                  const ids = groupOptions.map(({ id }) => id);

                  if (isActive) {
                    const value = [...props.value, ...ids];
                    return props.onChange(uniq(value));
                  }

                  props.onChange(without(props.value, ...ids));
                };

                return (
                  <div key={groupLabel}>
                    {index > 0 && <Divider sx={{ marginY: 1 }} />}
                    <FilterMenuItem
                      key={groupLabel}
                      isActive={isChecked}
                      isIndeterminate={isIndeterminate}
                      onChange={handleChangeGroup}
                    >
                      <Typography component="span" variant="subtitle2">
                        {groupLabel}
                      </Typography>
                    </FilterMenuItem>
                    {groupOptions.map((option) => {
                      const handleChangeOption = (isActive: boolean) => {
                        if (isActive) {
                          const value = [...props.value, option.id];
                          return props.onChange(value);
                        }

                        props.onChange(without(props.value, option.id));
                      };

                      return (
                        <FilterMenuItem
                          key={option.id}
                          level={1}
                          isActive={option.isActive}
                          onChange={handleChangeOption}
                        >
                          <Typography component="span" variant="body2">
                            {option.label} ({option.position}/
                            {option.totalStages})
                          </Typography>
                        </FilterMenuItem>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              opacity: scrollIndicator.isVisible ? 1 : 0,
              pointerEvents: scrollIndicator.isVisible ? 'auto' : 'none',
              transition: 'opacity 0.2s ease-in-out',
              zIndex: 1000,
            }}
          >
            <Box
              component="button"
              onClick={handleScrollToBottom}
              aria-label="Scroll to bottom of workflow stage list"
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: 'white',
                border: '1px solid',
                borderColor: 'grey.300',
                boxShadow: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'grey.50',
                },
              }}
            >
              <KeyboardArrowDown fontSize="small" color="action" />
            </Box>
          </Box>
        </Box>
      </Menu>
    </>
  );
}
